// Analytics tracking utility (GA4 / GTM dataLayer + First-Party)
import { sendVercelEvent } from './vercelEvents';
// All events use "vibe_" prefix for easy GTM regex tracking
// (Old InsightTrack experiment removed 2026-05-04 — no callers in prod.)

import { supabase } from '@/integrations/supabase/client';
import type { AnalyticsEvent, SessionData, EnhancedEventContext } from '@/types/analytics';
import { eventCapture } from './eventCapture';
import { safeSetItem, safeGetItem, safeRemoveItem } from '@/lib/utils';

/**
 * Minimal playlist shape required by ecommerce analytics functions.
 * Both the canonical `Playlist` type and Supabase Row type satisfy this.
 */
interface PlaylistLike {
  id: string;
  name: string;
  subscription_tier?: string | null;
  track_count: number;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    /**
     * GTM/GA4 gtag function. Loaded by GTM container (GTM-M9RZM3VN).
     * Undefined at runtime when consent is denied or GTM hasn't loaded yet —
     * call sites guard with `typeof window.gtag === 'function'` before use.
     *
     * Signature kept loose (`...args: unknown[]`) so this merged `Window`
     * augmentation stays byte-identical to the duplicate declaration in
     * CookieConsent.tsx. TS errors (TS2687/TS2717 "identical modifiers")
     * if the two `declare global` blocks disagree on type or optionality.
     */
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Read the GA4 client_id from the `_ga` cookie (format: `GA1.2.<clientId>`).
 * Returns null if gtag hasn't dropped the cookie yet OR if the user has
 * blocked/cleared cookies.
 *
 * Used by edge functions: client passes this in the request body, the edge
 * function forwards it to GA4 Measurement Protocol as `client_id`. Both the
 * client gtag events and the server MP events stitch to the SAME GA4 user,
 * so cross-source funnels work.
 *
 * Returns null cleanly — the caller should treat that as "no stitching"
 * and let the MP helper fall back to a random per-call UUID.
 */
export function getGaClientId(): string | null {
  if (typeof document === "undefined") return null;
  // _ga cookie format: GA1.<domain-depth>.<random>.<timestamp>
  // The "client_id" GA4 wants is the `<random>.<timestamp>` segment.
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA1\.\d+\.([\d.]+)/);
  return match ? match[1] : null;
}

// =====================================================
// FIRST-PARTY TRACKING INFRASTRUCTURE
// =====================================================

// Event batch queue
let eventBatchQueue: AnalyticsEvent[] = [];
let batchTimer: number | null = null;
const MAX_BATCH_SIZE = 50;
const BATCH_INTERVAL = 10000; // 10 seconds
let lastEventTime = Date.now();

// Session management
let currentSession: SessionData | null = null;
let sessionTimeout: number | null = null;
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

// Cached user id, kept in sync with Supabase auth via onAuthStateChange.
// Previously every event-batch flush called supabase.auth.getUser() —
// which makes a network round trip to verify the JWT — purely to attach
// userId to the payload. The edge function already authoritatively
// extracts user_id from the Authorization header that invoke() attaches,
// so the round trip was redundant. Caching here removes the cost without
// changing the data the edge function sees.
let cachedUserId: string | null = null;
let authSubscriptionInitialized = false;
function ensureAuthSubscription(): void {
  if (authSubscriptionInitialized) return;
  authSubscriptionInitialized = true;
  // Hydrate from current session synchronously where possible. getSession()
  // is local-only (reads from storage); no network call.
  void supabase.auth.getSession().then(({ data }) => {
    cachedUserId = data.session?.user?.id ?? null;
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedUserId = session?.user?.id ?? null;
  });
}

// Offline queue (IndexedDB fallback)
const OFFLINE_QUEUE_KEY = 'analytics_offline_queue';

/**
 * Get or create session ID
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

/**
 * The analytics session id, the unit the A/B arm is derived from
 * (src/lib/abTest.ts), so the arm and the funnel SQL count the same thing.
 */
export function getAnalyticsSessionId(): string {
  try {
    return getSessionId();
  } catch {
    return '';
  }
}

/**
 * Initialize or update session data
 */
function initializeSession(): SessionData {
  if (currentSession) {
    return currentSession;
  }

  const sessionId = getSessionId();
  
  // Safely get URL params
  let urlParams: URLSearchParams;
  try {
    urlParams = new URLSearchParams(window.location.search);
  } catch (_error) {
    // silent — non-critical
    urlParams = new URLSearchParams();
  }
  
  currentSession = {
    sessionId,
    startedAt: Date.now(),
    lastEventTime: Date.now(),
    pageViews: 0,
    eventsCount: 0,
    deviceType: getDeviceType(),
    referrer: document.referrer || undefined,
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined,
    utmContent: urlParams.get('utm_content') || undefined,
    utmTerm: urlParams.get('utm_term') || undefined,
    entryPage: window.location.pathname,
  };

  return currentSession;
}

/**
 * Declared crawlers and automation, matched against the user agent.
 *
 * Every token here was taken from a user_agent string already sitting in
 * analytics_events, not from a generic bot list. The dominant one is
 * `meta-externalagent`, Meta's AI crawler, which renders pages and therefore
 * runs our tracking exactly like a person would.
 *
 * `crawler` earns its place separately from `crawl` because several agents
 * only identify themselves in the documentation URL they append, for example
 * `(+https://developers.facebook.com/docs/sharing/webmasters/crawler)`.
 */
const BOT_USER_AGENT_RE =
  /bot\b|bot\/|crawler|crawl|spider|slurp|headlesschrome|lighthouse|externalagent|preview|scrapy|python-requests|curl\/|wget\//i;

/**
 * Is this visitor a crawler or an automated browser?
 *
 * Why this exists: between 2026-08-11 and 08-13, meta-externalagent indexed
 * playlist detail pages and produced 185 of the 195 page views recorded on
 * /playlists/* on 08-12, across 192 sessions with no referrer. When it left,
 * vibe_view_item fell 95% in a day. Nothing was broken and no users were
 * lost, but the drop was indistinguishable from a dead pixel until the user
 * agents were read one by one. Every rate computed from this table carried
 * that crawler in its denominator for three days.
 *
 * navigator.webdriver is checked first because it is a declaration rather
 * than a guess, and it catches headless automation that copies a normal
 * user agent. Our own Lighthouse cron is in that category: it loads 9 routes
 * twice a day, /pricing among them, so it was firing vibe_view_item into the
 * ecommerce funnel every morning at 04:02Z.
 *
 * Exported for tests. Deliberately conservative: a missed bot costs some
 * noise, a false positive silently deletes a real user's whole session.
 */
export function isLikelyBot(
  userAgent: string | undefined = typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  webdriver: boolean | undefined = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { webdriver?: boolean }).webdriver
    : undefined,
): boolean {
  if (webdriver === true) return true;
  if (!userAgent) return false;
  return BOT_USER_AGENT_RE.test(userAgent);
}

/**
 * Get device type
 */
function getDeviceType(): string {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/Mobile|Android|iPhone/i.test(ua)) return 'mobile';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  return 'desktop';
}

/**
 * Check if analytics consent is granted by the user (cookie-consent banner).
 * Used to gate any tracking that mirrors GDPR consent — including server-
 * side MP event sends, which accept a `consent` flag and strip identifiers
 * when false.
 *
 * Audit 2026-05-10: previously underscored as dead code while CookieConsent
 * relied solely on Google Consent Mode v2. Server-side MP doesn't run in
 * the browser (no gtag), so the consent state must be forwarded via every
 * edge-function call body that triggers MP. Public export below.
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const consent = safeGetItem('cookie-consent');
    if (!consent) return false;

    const preferences = JSON.parse(consent);
    return preferences.analytics === true;
  } catch (_error) {
    return false;
  }
}

/**
 * Get enhanced event context
 */
function getEnhancedContext(): Partial<EnhancedEventContext> {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  
  // Use modern Navigation Timing API (Level 2) or fallback
  let navigationStart = 0;
  try {
    if (performance.timeOrigin) {
      navigationStart = performance.timeOrigin;
    } else if (performance.timing?.navigationStart) {
      navigationStart = performance.timing.navigationStart;
    }
  } catch (_error) {
    // silent — non-critical
  }
  
  return {
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    scroll: {
      depth: eventCapture.getScrollDepth(),
      maxDepth: eventCapture.getMaxScrollDepth(),
    },
    timing: {
      sincePageLoad: navigationStart > 0 ? Date.now() - navigationStart : 0,
      sinceLastEvent: Date.now() - lastEventTime,
    },
    referrer: document.referrer || '',
    utm: {
      source: urlParams.get('utm_source') || undefined,
      medium: urlParams.get('utm_medium') || undefined,
      campaign: urlParams.get('utm_campaign') || undefined,
      content: urlParams.get('utm_content') || undefined,
      term: urlParams.get('utm_term') || undefined,
    },
  };
}

/**
 * Track event to first-party analytics
 */
async function trackToFirstParty(
  eventName: string,
  eventCategory: string,
  eventAction: string,
  eventLabel?: string,
  additionalParams?: Record<string, unknown>
): Promise<void> {
  if (typeof window === 'undefined') return;

  // Crawlers and headless automation never become users, so counting them as
  // sessions only dilutes every rate computed downstream. See isLikelyBot for
  // the incident that prompted this. Deciding here rather than at each call
  // site because this is the single chokepoint for analytics_events writes.
  //
  // This used to `return` and write nothing. It flags instead, because the
  // incident behind isLikelyBot was diagnosed by reading user agents out of
  // this table and dropping the row destroys exactly that evidence: on
  // 2026-08-22 a crawler produced 61 sessions in four hours and could not be
  // identified afterwards. Rate-computing readers use analytics_events_human,
  // which excludes these rows. Cost of the change is that bot traffic now
  // reaches the batch endpoint at all, which it previously never did.
  const isBot = isLikelyBot();

  // First-party analytics uses PII scrubbing and is owned infrastructure —
  // no consent gate needed (legitimate interest, like server access logs).
  // GA4/marketing consent is handled separately in the trackEvent GTM push.

  // Initialize session
  const session = initializeSession();
  const context = getEnhancedContext();

  // Extract element data from additionalParams (populated by eventCapture)
  const params = additionalParams || {};
  const elementSelector = (params.selector as string) || undefined;
  const elementText = (params.text as string) || undefined;
  const xPos = typeof params.xPosition === 'number' ? params.xPosition : undefined;
  const yPos = typeof params.yPosition === 'number' ? params.yPosition : undefined;

  // Create event
  const event: AnalyticsEvent = {
    eventName,
    eventCategory,
    eventAction,
    eventLabel,
    eventProperties: params,
    sessionId: session.sessionId,
    pagePath: window.location.pathname,
    pageTitle: document.title,
    referrer: document.referrer || undefined,
    userAgent: navigator.userAgent,
    isBot,
    deviceType: session.deviceType,
    viewportSize: context.viewport,
    scrollDepth: context.scroll?.depth,
    elementSelector,
    elementText,
    xPosition: xPos,
    yPosition: yPos,
    timestamp: new Date().toISOString(),
  };

  // Add to batch queue
  eventBatchQueue.push(event);
  session.eventsCount++;
  session.lastEventTime = Date.now();
  lastEventTime = Date.now();

  // Reset session timeout
  if (sessionTimeout) clearTimeout(sessionTimeout);
  sessionTimeout = window.setTimeout(() => {
    flushEventBatch(true); // Flush on timeout
  }, SESSION_TIMEOUT);

  // Flush if batch is full or on critical events
  const isCriticalEvent = eventCategory === 'authentication' || 
                         eventName.includes('purchase') ||
                         eventName.includes('error');

  if (eventBatchQueue.length >= MAX_BATCH_SIZE || isCriticalEvent) {
    await flushEventBatch();
  } else if (!batchTimer) {
    // Schedule batch flush
    batchTimer = window.setTimeout(() => {
      flushEventBatch();
    }, BATCH_INTERVAL);
  }
}

/**
 * Flush event batch to server
 */
async function flushEventBatch(isSessionEnd = false): Promise<void> {
  if (eventBatchQueue.length === 0) return;

  const eventsToSend = [...eventBatchQueue];
  eventBatchQueue = [];

  if (batchTimer) {
    clearTimeout(batchTimer);
    batchTimer = null;
  }

  // Lazy-init the auth subscription on first flush so we avoid touching
  // supabase.auth before any event has actually fired (cheap when this
  // runs but pointless if analytics are gated off via tracking-toggles).
  ensureAuthSubscription();

  try {
    // Use the cached user_id (kept fresh by onAuthStateChange). Avoids
    // the per-flush network round trip to supabase.auth.getUser(). The
    // edge function still authoritatively extracts user_id from the
    // Authorization header attached by invoke() — this id is purely for
    // the sessionData echo back into our own session record.
    const userId = cachedUserId;

    const response = await supabase.functions.invoke('track-analytics-batch', {
      body: {
        events: eventsToSend,
        sessionData: currentSession
          ? { ...currentSession, userId: userId ?? undefined }
          : undefined,
      },
    });

    if (response.error) {
      // Silently queue for retry — analytics errors should never spam the console
      saveToOfflineQueue(eventsToSend);
    } else {
      // Try to send offline queue if it exists
      await processOfflineQueue();
    }
  } catch (_error) {
    // Silently save to offline queue — network failures are expected (offline, CORS, etc.)
    saveToOfflineQueue(eventsToSend);
  }

  // End session if needed
  if (isSessionEnd) {
    currentSession = null;
    sessionStorage.removeItem('analytics_session_id');
  }
}

/**
 * Save events to offline queue (localStorage fallback)
 */
function saveToOfflineQueue(events: AnalyticsEvent[]): void {
  try {
    const existing = safeGetItem(OFFLINE_QUEUE_KEY);
    const queue = existing ? JSON.parse(existing) : [];
    queue.push(...events);
    
    // Limit queue size to prevent localStorage quota exceeded
    if (queue.length > 500) {
      queue.splice(0, queue.length - 500);
    }
    
    safeSetItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      try { safeRemoveItem(OFFLINE_QUEUE_KEY); } catch (_e) { /* noop */ }
    }
    // Silently fail — analytics storage errors are non-critical
  }
}

/**
 * Process offline queue
 */
async function processOfflineQueue(): Promise<void> {
  try {
    const queueData = safeGetItem(OFFLINE_QUEUE_KEY);
    if (!queueData) return;

    const queue: AnalyticsEvent[] = JSON.parse(queueData);
    if (!Array.isArray(queue) || queue.length === 0) return;

    const { error } = await supabase.functions.invoke('track-analytics-batch', {
      body: {
        events: queue,
      },
    });

    if (!error) {
      safeRemoveItem(OFFLINE_QUEUE_KEY);
    }
  } catch (_error) {
    // Silently fail — offline queue will be retried later
  }
}

/**
 * Initialize first-party analytics
 * Returns a cleanup function to remove listeners
 */
export function initializeFirstPartyAnalytics(): (() => void) | undefined {
  if (typeof window === 'undefined') return;

  // Suppress analytics inside admin preview iframes
  if (new URLSearchParams(window.location.search).has('_preview')) return;

  // Initialize event capture
  eventCapture.initialize((capturedEvent) => {
    const eventType = capturedEvent.type as string;

    // Map captured events to analytics events
    if (eventType === 'click') {
      trackToFirstParty(
        'vibe_click',
        'click',
        'element_click',
        capturedEvent.selector as string,
        capturedEvent
      );
    } else if (eventType === 'scroll') {
      trackToFirstParty(
        'vibe_scroll',
        'scroll',
        'page_scroll',
        undefined,
        capturedEvent
      );
    } else if (eventType === 'form_submit') {
      trackToFirstParty(
        'vibe_form_submit',
        'form',
        'submit',
        capturedEvent.formId as string,
        capturedEvent
      );
    } else if (eventType === 'rage_click') {
      trackToFirstParty(
        'vibe_rage_click',
        'engagement',
        'rage_click',
        capturedEvent.selector as string,
        capturedEvent
      );
    } else if (eventType === 'dead_click') {
      trackToFirstParty(
        'vibe_dead_click',
        'engagement',
        'dead_click',
        capturedEvent.selector as string,
        capturedEvent
      );
    }
  });

  // Track initial page view
  trackToFirstParty(
    'vibe_page_view',
    'page_view',
    'view',
    window.location.pathname
  );

  // Flush on page unload — use fetch with keepalive (survives page close, supports headers)
  const beforeUnloadHandler = () => {
    if (eventBatchQueue.length === 0) return;
    const eventsToSend = [...eventBatchQueue];
    eventBatchQueue = [];
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim();
    const anonKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim();
    if (supabaseUrl && anonKey) {
      const payload = JSON.stringify({
        events: eventsToSend,
        sessionData: currentSession ?? undefined,
      });
      // keepalive: true lets the request outlive the page (unlike plain fetch)
      // sendBeacon can't send custom headers, but the edge relay needs apikey
      fetch(`${supabaseUrl}/functions/v1/track-analytics-batch`, {
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`,
        },
        keepalive: true,
      }).catch(() => {}); // fire-and-forget
    }
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);
  // iOS Safari does not fire beforeunload. A checkout whose popup is blocked
  // falls back to a same-tab redirect, and without pagehide that batch (the
  // paywall impression and checkout_started) never left the page. The handler
  // empties the queue, so a browser that fires both sends once.
  window.addEventListener('pagehide', beforeUnloadHandler);

  // Process offline queue on init
  processOfflineQueue();

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    window.removeEventListener('pagehide', beforeUnloadHandler);
    eventCapture.cleanup();
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      sessionTimeout = null;
    }
  };
}

export const trackEvent = (
  eventAction: string,
  eventCategory: string,
  eventLabel?: string,
  additionalParams?: Record<string, unknown>,
  /**
   * skipDataLayer: the caller has already sent this exact event to GA4 by
   * another transport (a direct gtag call), so pushing it here as well would
   * have GTM forward a second copy of the same event. First-party tracking
   * still runs either way.
   */
  options?: { skipDataLayer?: boolean }
) => {
  if (typeof window !== 'undefined') {
    const eventName = `vibe_${eventAction}`;

    // Track to GA4 via GTM. CRITICAL: dataLayer.push runs GTM tags
    // synchronously, so a GTM tag that throws (e.g. a Zod-style
    // 'Invalid data: Key Name: event' validator) would bubble out of
    // this call into the caller — same failure pattern as the UET-on-
    // login bug (PR #104). Swallow any tag-side throw so the click
    // handler keeps running.
    if (window.dataLayer && !options?.skipDataLayer) {
      try {
        window.dataLayer.push({
          event: eventName,
          event_category: eventCategory,
          event_action: eventAction,
          event_label: eventLabel || '',
          ...additionalParams,
        });
      } catch (err) {
         
        console.warn('[trackEvent] dataLayer.push threw (GTM tag), swallowed:', err);
      }
    }

    // Track to first-party analytics
    try {
      trackToFirstParty(
        eventName,
        eventCategory,
        eventAction,
        eventLabel,
        additionalParams
      );
    } catch (err) {
       
      console.warn('[trackEvent] trackToFirstParty threw, swallowed:', err);
    }

    // Third sink: Vercel Web Analytics, but ONLY for the conversion allowlist
    // in vercelEvents.ts. Deliberately not gated on skipDataLayer -- that flag
    // exists to stop GTM forwarding a duplicate to GA4, which says nothing
    // about whether Vercel should receive it. Self-gating and self-swallowing.
    try {
      sendVercelEvent(eventName, additionalParams);
    } catch (err) {
       
      console.warn('[trackEvent] sendVercelEvent threw, swallowed:', err);
    }
  }
};

// Set user ID for cross-domain tracking — no-op now that InsightTrack is gone.
// Kept as an exported function so existing callers don't break; once we audit
// callers we can drop the function and the imports.
export const setUserId = async (_userId: string, _email?: string) => {
  // intentionally empty
};

// Navigation tracking
export const trackNavigation = (destination: string, source: string = 'header') => {
  trackEvent('navigation_click', 'navigation', `${source}_to_${destination}`);
};

// CTA tracking. ONE event.
//
// This used to fire twice, on the belief that the first call emitted a
// "legacy" unprefixed `cta_click` and the second added the namespaced
// `vibe_cta_click`. That premise was wrong: trackEvent always prefixes, so the
// first call was already the namespaced event and the second produced
// `vibe_vibe_cta_click` — a malformed duplicate, not a legacy name. Every CTA
// click was counted twice in GA4 and the junk name polluted the event
// registry. The params the second call carried are kept here, so nothing that
// reads cta_name / cta_location loses its dimension.
export const trackCTA = (ctaName: string, location: string) => {
  trackEvent('cta_click', 'engagement', `${location}_${ctaName}`, {
    cta_name: ctaName,
    cta_location: location,
  });
};

/**
 * Outbound-CTA helper — fires `vibe_cta_click` and waits up to 2s for the
 * GA4 beacon to flush before navigating. Use for any click that opens an
 * external URL or routes hard (window.location.href = ...). Without this,
 * the unload kills the beacon ~30% of the time depending on connection.
 *
 * Pattern from GA4 docs (https://developers.google.com/tag-platform/...).
 *
 * Usage in React:
 *   <button onClick={() => trackCtaThenNavigate('https://open.spotify.com/...', 'spotify_open', 'playlist_card')}>
 *     Open in Spotify
 *   </button>
 *
 * Returns a Promise that resolves when the navigation fires, so the caller
 * can `await` it for testability — but in normal usage you don't need to.
 */
export const trackCtaThenNavigate = (url: string, ctaName: string, location: string): Promise<void> => {
  return new Promise((resolve) => {
    let navigated = false;
    const navigate = () => {
      if (navigated) return;
      navigated = true;
      window.location.href = url;
      resolve();
    };
    // Hard timeout — never delay the user more than 2s even if GA4 hangs.
    const timeoutId = window.setTimeout(navigate, 2000);
    // Fire both events; the gtag call carries event_callback which fires
    // when the beacon lands. trackEvent below dual-fires for the legacy
    // name, but only the gtag-prefixed call gets the callback.
    // Wrapped because a throwing gtag would reject this Promise, skip the
    // first-party trackEvent below, and leave the user staring at a dead button
    // for the full 2s timeout before navigating. Same class as the unguarded
    // consent branches in conversions.ts (S16-AUD-101): a vendor SDK call in a
    // click path is never allowed to decide whether the app's own work happens.
    let sentViaGtag = false;
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        sentViaGtag = true;
        window.gtag('event', 'vibe_cta_click', {
          cta_name: ctaName,
          cta_location: location,
          event_callback: () => {
            window.clearTimeout(timeoutId);
            navigate();
          },
          event_timeout: 2000,
        });
      }
    } catch (err) {
      sentViaGtag = false;
      console.warn('[trackCtaThenNavigate] gtag threw, navigating anyway:', err);
      window.clearTimeout(timeoutId);
      navigate();
    }
    // First-party always; GA4 only if the gtag call above did not already
    // send it. Both paths emit `vibe_cta_click`, so doing both counted every
    // outbound CTA twice.
    trackEvent(
      'cta_click',
      'engagement',
      `${location}_${ctaName}`,
      { cta_name: ctaName, cta_location: location },
      { skipDataLayer: sentViaGtag },
    );
  });
};

// Authentication tracking — vibe_sign_up / vibe_sign_in / vibe_sign_out.
//
// Same defect as trackCTA had: the second call passed an already-prefixed
// name into a function that prefixes, so every sign-in also emitted
// `vibe_vibe_sign_in`. One event, carrying the method param the second call
// used to add.
export const trackAuth = (action: 'sign_in' | 'sign_up' | 'sign_out', method?: string) => {
  trackEvent(action, 'authentication', method, { method });
};

// Playlist tracking
export const trackPlaylist = (action: string, playlistId?: string, details?: string) => {
  trackEvent(action, 'playlist', details, { playlist_id: playlistId });
};

// Generation tracking
export const trackGeneration = (action: string, model?: string, trackCount?: number) => {
  trackEvent(action, 'playlist_generation', model, { track_count: trackCount });
};

// Subscription tracking
export const trackSubscription = (action: string, plan?: string) => {
  trackEvent(action, 'subscription', plan);
};

// Form tracking
export const trackForm = (formName: string, action: 'submit' | 'error' | 'success') => {
  trackEvent(`form_${action}`, 'form', formName);
};

// Content tracking
export const trackContent = (action: string, contentType: string, contentName: string) => {
  trackEvent(action, contentType, contentName);
};

// Purchase tracking
//
// CRITICAL: GA4's built-in ecommerce reports (Monetization, ROAS, ad
// attribution) require the event name to be EXACTLY `purchase`. Firing
// only `purchase_complete` (custom name) leaves those reports blank —
// which broke ad-attribution dashboards in prod until 2026-05-05.
//
// We dual-fire here so:
// - Legacy `purchase_complete` reports keep working (existing GTM rules
//   and historical data).
// - GA4 ecommerce + Google Ads conversion linking pick up `purchase`
//   with the canonical params (transaction_id, value, currency, items).
//
// Server-side Measurement Protocol fires the same `purchase` event from
// the Stripe webhook (PR #50) — this is the client-side companion for
// gtag-based ad pixels (Meta Pixel, UET) that listen for the event.
export const trackPurchase = (
  plan: string,
  amount?: number,
  options?: { transactionId?: string; currency?: string },
) => {
  const params = {
    value: amount,
    currency: options?.currency ?? 'EUR',
    transaction_id: options?.transactionId,
    items: [
      {
        item_id: plan,
        item_name: plan,
        item_category: 'subscription',
        price: amount,
        quantity: 1,
      },
    ],
  };
  // Standard GA4 ecommerce event — feeds Monetization reports + Google Ads
  // conversion linking.
  trackEvent('purchase', 'ecommerce', plan, params);
  // Legacy custom name — keeps existing dashboards alive.
  trackEvent('purchase_complete', 'ecommerce', plan, params);
};

// Ecommerce tracking for playlists (treating playlists as items)
export const trackViewItemList = (playlists: PlaylistLike[]) => {
  const items = playlists.map((playlist, index) => ({
    item_id: playlist.id,
    item_name: playlist.name,
    item_category: 'playlist',
    item_variant: playlist.subscription_tier,
    price: playlist.subscription_tier === 'pro' ? 5.0 : 0,
    quantity: playlist.track_count,
    index,
  }));

  trackEvent('view_item_list', 'ecommerce', 'playlists_page', {
    item_list_id: 'user_playlists',
    item_list_name: 'My Playlists',
    items,
  });
};

export const trackViewItem = (playlist: PlaylistLike) => {
  const value = playlist.subscription_tier === 'pro' ? 5.0 : 0;

  trackEvent('view_item', 'ecommerce', playlist.name, {
    value,
    currency: 'EUR',
    items: [{
      item_id: playlist.id,
      item_name: playlist.name,
      item_category: 'playlist',
      item_variant: playlist.subscription_tier,
      price: value,
      quantity: playlist.track_count,
    }],
  });
};

export const trackSelectItem = (playlist: PlaylistLike, listName: string = 'My Playlists') => {
  trackEvent('select_item', 'ecommerce', playlist.name, {
    item_list_id: 'user_playlists',
    item_list_name: listName,
    items: [{
      item_id: playlist.id,
      item_name: playlist.name,
      item_category: 'playlist',
      item_variant: playlist.subscription_tier,
      price: playlist.subscription_tier === 'pro' ? 5.0 : 0,
      quantity: playlist.track_count,
    }],
  });
};

export const trackPlaylistAction = (
  action: 'create' | 'refine' | 'export_spotify' | 'export_youtube' | 'delete',
  playlist: PlaylistLike
) => {
  const actionValues = {
    create: 10,
    refine: 3,
    export_spotify: 2,
    export_youtube: 4,
    delete: -1,
  };

  trackEvent(`playlist_${action}`, 'ecommerce', playlist.name, {
    value: actionValues[action],
    currency: 'EUR',
    items: [{
      item_id: playlist.id,
      item_name: playlist.name,
      item_category: 'playlist',
      item_variant: playlist.subscription_tier,
      quantity: playlist.track_count,
    }],
  });
};

// Share tracking
export const trackShare = (platform: string, contentType: string, contentId?: string) => {
  trackEvent('share', 'social', `${platform}_${contentType}`, { content_id: contentId });
};

// Feature usage tracking
export const trackFeature = (feature: string, action: string, detail?: string) => {
  trackEvent(`feature_${action}`, 'feature', feature, { detail });
};

// Performance tracking - measures component render times, gesture latency, etc.
export const trackPerformance = (
  metricName: string, 
  value: number, 
  metadata?: Record<string, unknown>
) => {
  trackEvent('performance_metric', 'performance', metricName, {
    value,
    metric_name: metricName,
    ...metadata,
  });
};

// Gesture latency tracking
export const trackGestureLatency = (
  gestureType: string, 
  duration: number,
  metadata?: Record<string, unknown>
) => {
  trackEvent('gesture_latency', 'interaction', gestureType, {
    duration,
    gesture_type: gestureType,
    ...metadata,
  });
};

// Modal performance tracking
export const trackModalPerformance = (
  action: 'open' | 'close',
  timing: number,
  metadata?: Record<string, unknown>
) => {
  trackEvent(`modal_${action}`, 'performance', `${action}_time`, {
    timing,
    ...metadata,
  });
};

// GA4 ecommerce events extracted to analyticsEcommerce.ts — re-exported for backward compatibility
export { ga4ViewItem, ga4BeginCheckout, ga4Purchase } from './analyticsEcommerce';

